import { jsx as _jsx } from "react/jsx-runtime";
import { cva } from "class-variance-authority";
import { Slot } from "radix-ui";
import { cn } from "../../lib/cn";
/**
 * The brand's button, measured from `.btn` in the Data Intelligence page.
 *
 * Three things are load bearing and should not drift:
 *
 *   - Mono, uppercase, `0.07em` tracking. Buttons are labels, and the brand
 *     sets every label in Aeonik Fono.
 *   - `min-height: 44px` (`min-h-11`), the touch target floor.
 *   - The primary hover INVERTS to white with black text rather than
 *     darkening. `--lg-pink-dark` exists for a darkened step and the brand
 *     never uses it.
 *
 * A naming trap worth knowing: the source page calls its bordered variant
 * `.btn--ghost`. shadcn calls a bordered button `outline` and a borderless one
 * `ghost`. This follows shadcn, because that is what everyone reaching for
 * `<Button variant="ghost">` will expect, so the brand's `.btn--ghost` is
 * `variant="outline"` here.
 */
export const buttonVariants = cva([
    "lg:inline-flex lg:items-center lg:justify-center lg:gap-2 lg:whitespace-nowrap",
    "lg:rounded-pill lg:border lg:border-transparent",
    "lg:font-label lg:uppercase",
    "lg:cursor-pointer lg:transition-colors lg:duration-150",
    "lg:outline-none lg:focus-visible:ring-2 lg:focus-visible:ring-ring",
    "lg:focus-visible:ring-offset-2 lg:focus-visible:ring-offset-background",
    "lg:disabled:pointer-events-none lg:disabled:opacity-50",
    "lg:[&_svg]:size-4 lg:[&_svg]:shrink-0",
], {
    variants: {
        variant: {
            primary: "lg:bg-primary lg:text-primary-foreground lg:hover:bg-white lg:hover:text-black",
            outline: "lg:border-hairline-2 lg:text-foreground lg:hover:border-strong lg:hover:text-strong",
            ghost: "lg:text-muted-foreground lg:hover:bg-secondary lg:hover:text-strong",
            subtle: "lg:bg-secondary lg:border-hairline lg:text-foreground lg:hover:border-hairline-2 lg:hover:text-strong",
            destructive: "lg:bg-destructive lg:text-destructive-foreground lg:hover:bg-white lg:hover:text-black",
            link: "lg:text-primary lg:underline-offset-4 lg:hover:underline lg:rounded-xs",
        },
        size: {
            sm: "lg:min-h-9 lg:px-4.5 lg:text-button-sm",
            md: "lg:min-h-11 lg:px-6 lg:text-button",
            lg: "lg:min-h-13 lg:px-8 lg:text-button",
            icon: "lg:size-11 lg:px-0",
        },
        block: {
            true: "lg:flex lg:w-full",
            false: "",
        },
    },
    compoundVariants: [
        // A link button is type, not a control, so the pill metrics get in the
        // way of putting it inline in a sentence.
        { variant: "link", size: "md", class: "lg:min-h-0 lg:px-0" },
        { variant: "link", size: "sm", class: "lg:min-h-0 lg:px-0" },
        { variant: "link", size: "lg", class: "lg:min-h-0 lg:px-0" },
    ],
    defaultVariants: { variant: "primary", size: "md", block: false },
});
export function Button({ className, variant, size, block, asChild = false, type, ...props }) {
    const Component = asChild ? Slot.Root : "button";
    return (_jsx(Component, { "data-slot": "button", ...(asChild ? {} : { type: type ?? "button" }), className: cn(buttonVariants({ variant, size, block }), className), ...props }));
}
//# sourceMappingURL=button.js.map